import { AquaApi, AquaApiError, InspectResponse, VerifyFile, toBase64 } from '../api'
import { el, clear } from '../dom'

export interface VerifyTabOptions {
  api: AquaApi
  onVerified: (detail: unknown) => void
  onError: (err: AquaApiError) => void
}

interface Held {
  trees: unknown[]           // every aqua tree the user has supplied (main + linked)
  selective?: unknown        // set if the main artifact is a redacted/selective tree
  files: Map<string, { name: string; b64: string }> // content_hash(lowercase) -> file
}

export class VerifyTab {
  readonly root = el('div', { class: 'verify' })
  private held: Held = { trees: [], files: new Map() }

  constructor(private o: VerifyTabOptions) {
    this.renderStart()
  }

  private showError(e: unknown) {
    const err = e instanceof AquaApiError ? e : new AquaApiError(0, String((e as Error).message))
    this.root.append(el('div', { class: 'err' }, err.message))
    this.o.onError(err)
  }

  private renderStart() {
    clear(this.root)
    this.held = { trees: [], files: new Map() }
    const drop = el('div', { class: 'drop' }, 'Drop an aqua artifact (.aqua.json) to verify')
    const input = el('input', { type: 'file', accept: '.json,application/json' }) as HTMLInputElement
    input.style.display = 'none'
    drop.addEventListener('click', () => input.click())
    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.classList.add('over') })
    drop.addEventListener('dragleave', () => drop.classList.remove('over'))
    drop.addEventListener('drop', (e) => {
      e.preventDefault()
      drop.classList.remove('over')
      const f = (e as DragEvent).dataTransfer?.files?.[0]
      if (f) this.loadArtifact(f)
    })
    input.addEventListener('change', () => { const f = input.files?.[0]; if (f) this.loadArtifact(f) })
    this.root.append(drop, input)
  }

  private async loadArtifact(file: File) {
    try {
      const json = JSON.parse(await file.text())
      // A selective tree is revision-tagged (each revision carries a `disclosure` field).
      const revs: Record<string, unknown> = json.revisions ?? {}
      const isSelective = Object.values(revs).some((r: unknown) => r !== null && typeof r === 'object' && 'disclosure' in (r as object))
      if (isSelective) {
        this.held.selective = json
        await this.verifySelective()
      } else {
        this.held.trees = [json]
        await this.refreshChecklist()
      }
    } catch (e) { this.showError(e) }
  }

  private async refreshChecklist() {
    let inspect: InspectResponse
    try {
      inspect = await this.o.api.inspect({
        trees: this.held.trees,
        have_file_hashes: [...this.held.files.keys()],
      })
    } catch (e) { this.showError(e); return }
    this.renderChecklist(inspect)
  }

  private renderChecklist(inspect: InspectResponse) {
    clear(this.root)
    this.root.append(el('div', { class: 'muted' }, 'Attach all required items, then verify:'))

    for (const rf of inspect.required_files) {
      this.root.append(this.fileRow(rf))
    }
    for (const rt of inspect.required_trees) {
      this.root.append(this.treeRow(rt))
    }

    const verifyBtn = el('button', { class: 'act' }, 'Verify') as HTMLButtonElement
    verifyBtn.addEventListener('click', () => this.runVerify())
    if (!inspect.satisfied) verifyBtn.disabled = true

    const startOver = el('button', { class: 'act ghost' }, 'Start over')
    startOver.addEventListener('click', () => this.renderStart())
    this.root.append(el('div', { class: 'row' }, verifyBtn, startOver))
  }

  private fileRow(rf: { content_hash: string; file_name: string; present: boolean }): HTMLElement {
    const status = el('span', { class: rf.present ? 'ok' : 'miss' }, rf.present ? '✓' : '•')
    const label = el('span', {}, rf.file_name)
    const row = el('div', { class: 'check' }, status, label)
    if (!rf.present) {
      const input = el('input', { type: 'file' }) as HTMLInputElement
      input.style.display = 'none'
      const pick = el('button', { class: 'act ghost' }, 'Attach')
      pick.addEventListener('click', () => input.click())
      input.addEventListener('change', async () => {
        const f = input.files?.[0]; if (!f) return
        const b64 = toBase64(await f.arrayBuffer())
        this.held.files.set(rf.content_hash.toLowerCase(), { name: f.name, b64 })
        await this.refreshChecklist()
      })
      row.append(input, pick)
    }
    return row
  }

  private treeRow(rt: { hash: string; present: boolean }): HTMLElement {
    const status = el('span', { class: rt.present ? 'ok' : 'miss' }, rt.present ? '✓' : '•')
    const label = el('span', {}, `linked tree ${rt.hash.slice(0, 10)}…`)
    const row = el('div', { class: 'check' }, status, label)
    if (!rt.present) {
      const input = el('input', { type: 'file', accept: '.json' }) as HTMLInputElement
      input.style.display = 'none'
      const pick = el('button', { class: 'act ghost' }, 'Attach tree')
      pick.addEventListener('click', () => input.click())
      input.addEventListener('change', async () => {
        const f = input.files?.[0]; if (!f) return
        try {
          const json = JSON.parse(await f.text())
          this.held.trees.push(json)       // grows the set; re-inspect reveals deeper deps
          await this.refreshChecklist()
        } catch (e) { this.showError(e) }
      })
      row.append(input, pick)
    }
    return row
  }

  private async runVerify() {
    const files: VerifyFile[] = [...this.held.files.values()].map((f) => ({ file_name: f.name, bytes_base64: f.b64 }))
    const [main, ...linked] = this.held.trees
    try {
      const res = await this.o.api.verify({ kind: 'tree', main, linked_trees: linked, files, check_claims: false })
      this.renderVerdict(res)
      this.o.onVerified(res)
    } catch (e) { this.showError(e) }
  }

  private async verifySelective() {
    try {
      const res = await this.o.api.verify({ kind: 'selective', main: this.held.selective })
      this.renderVerdict(res)
      this.o.onVerified(res)
    } catch (e) { this.showError(e) }
  }

  private renderVerdict(res: { is_valid: boolean; status: string; logs: unknown[]; per_file?: { file_name: string; matched: boolean }[] }) {
    clear(this.root)
    this.root.append(
      el('div', { class: `badge ${res.is_valid ? 'valid' : 'invalid'}` }, res.is_valid ? 'Valid ✓' : 'Invalid ✗'),
      el('div', { class: 'muted' }, res.status),
    )
    if (res.per_file?.length) {
      for (const pf of res.per_file) {
        this.root.append(el('div', { class: 'check' },
          el('span', { class: pf.matched ? 'ok' : 'miss' }, pf.matched ? '✓' : '✗'),
          el('span', {}, pf.file_name)))
      }
    }
    if (res.logs?.length) {
      this.root.append(el('div', { class: 'log' }, JSON.stringify(res.logs, null, 2)))
    }
    const again = el('button', { class: 'act ghost' }, 'Verify another')
    again.addEventListener('click', () => this.renderStart())
    this.root.append(el('div', { class: 'row' }, again))
  }
}
